import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { StammkundeService } from '../../../Services/stammkunde.service';
import { CaptureQueueService } from '../../../Services/capture-queue.service';
import { Auftrag, ZeitEintrag, ZeitEintragCreate } from '../../../Models/stammkunde.model';

/** Eine wählbare Person für die Stundenerfassung (Login-Benutzer ODER Arbeiter ohne Login). */
export interface ZeitPerson {
  label: string;
  workerId?: number | null;
  personUserId?: string | null;
}

export interface ZeitEintragFormData {
  auftrag: Auftrag;
  assignmentId?: number | null;
  /** Auswahl von Personen (Crew des Einsatzes bzw. Team des Stammkunden). */
  people: ZeitPerson[];
  /** Bearbeiten eines bestehenden Eintrags. */
  entry?: ZeitEintrag;
  /** Offline-fähig erfassen (Vorarbeiter im Feld) – nur beim Neuanlegen. */
  useQueue?: boolean;
  /** Vorbelegtes Datum (yyyy-MM-dd). */
  defaultDate?: string;
}

interface PersonOption {
  key: string;
  label: string;
  workerId?: number | null;
  personUserId?: string | null;
}

const FREE_KEY = '__free__';

/**
 * Dialog zum Erfassen/Bearbeiten eines Stunden-Eintrags (ohne Preise). Beim Erfassen
 * im Feld (useQueue) wird bei fehlender Verbindung offline zwischengespeichert.
 */
@Component({
  selector: 'app-zeit-eintrag-form',
  templateUrl: './zeit-eintrag-form.component.html',
  styleUrls: ['./zeit-eintrag-form.component.css', '../stammkunden-theme.css']
})
export class ZeitEintragFormComponent {
  readonly freeKey = FREE_KEY;
  isEdit: boolean;
  saving = false;

  personOptions: PersonOption[] = [];
  selectedPersonKey = '';
  freeName = '';
  workDate = '';
  hours: number | null = null;
  note = '';

  constructor(
    public dialogRef: MatDialogRef<ZeitEintragFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ZeitEintragFormData,
    private stammkundeService: StammkundeService,
    private captureQueue: CaptureQueueService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.entry;
    this.buildPersonOptions();

    const e = data.entry;
    if (e) {
      this.workDate = this.toDateInput(new Date(e.workDate));
      this.hours = e.hours;
      this.note = e.note ?? '';
      this.selectedPersonKey = this.keyForEntry(e);
      if (this.selectedPersonKey === FREE_KEY) {
        this.freeName = e.personName;
      }
    } else {
      this.workDate = data.defaultDate ?? this.toDateInput(new Date());
      if (this.personOptions.length === 1) {
        this.selectedPersonKey = this.personOptions[0].key;
      }
    }
  }

  get canQueue(): boolean {
    return !!this.data.useQueue && !this.isEdit;
  }

  get isOffline(): boolean {
    return !this.captureQueue.isOnline;
  }

  get valid(): boolean {
    if (!this.workDate || !this.hours || this.hours <= 0) { return false; }
    if (this.selectedPersonKey === FREE_KEY) { return !!this.freeName.trim(); }
    return !!this.selectedPersonKey;
  }

  async save(): Promise<void> {
    if (!this.valid || this.saving) { return; }
    this.saving = true;

    const payload = this.buildPayload();

    try {
      if (this.isEdit) {
        const updated = await firstValueFrom(this.stammkundeService
          .updateTimeEntry(this.data.auftrag.id, this.data.entry!.id, payload));
        this.dialogRef.close({ ok: true, queued: false, entry: updated });
        return;
      }

      if (this.canQueue) {
        const label = `${this.hours} h · ${this.personLabel()} · ${this.data.auftrag.title}`;
        const res = await this.captureQueue.captureTimeEntry(
          this.data.auftrag.id, payload.assignmentId ?? null, payload, label);
        this.dialogRef.close({ ok: true, queued: res.queued, entry: res.result });
        return;
      }

      const created = await firstValueFrom(this.stammkundeService
        .addTimeEntry(this.data.auftrag.id, payload));
      this.dialogRef.close({ ok: true, queued: false, entry: created });
    } catch (err: any) {
      this.saving = false;
      const message = err?.error?.message
        || (typeof err?.error === 'string' && err.error)
        || 'Stunden konnten nicht gespeichert werden.';
      this.snackBar.open(message, 'OK', { duration: 5000 });
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private buildPayload(): ZeitEintragCreate {
    const payload: ZeitEintragCreate = {
      assignmentId: this.data.assignmentId ?? this.data.entry?.assignmentId ?? null,
      workDate: `${this.workDate}T00:00:00`,
      hours: this.hours!,
      note: this.note.trim() || undefined
    };
    if (this.selectedPersonKey === FREE_KEY) {
      payload.personName = this.freeName.trim();
    } else {
      const opt = this.personOptions.find(o => o.key === this.selectedPersonKey);
      if (opt?.workerId != null) {
        payload.workerId = opt.workerId;
      } else if (opt?.personUserId) {
        payload.personUserId = opt.personUserId;
      } else {
        payload.personName = opt?.label;
      }
    }
    return payload;
  }

  private personLabel(): string {
    if (this.selectedPersonKey === FREE_KEY) { return this.freeName.trim(); }
    return this.personOptions.find(o => o.key === this.selectedPersonKey)?.label ?? '';
  }

  private buildPersonOptions(): void {
    const seen = new Set<string>();
    const options: PersonOption[] = [];
    for (const p of this.data.people ?? []) {
      const key = p.workerId != null ? `w:${p.workerId}`
        : p.personUserId ? `u:${p.personUserId}`
          : `n:${p.label}`;
      if (seen.has(key)) { continue; }
      seen.add(key);
      options.push({ key, label: p.label, workerId: p.workerId, personUserId: p.personUserId });
    }
    this.personOptions = options;
  }

  private keyForEntry(e: ZeitEintrag): string {
    if (e.workerId != null) {
      const opt = this.personOptions.find(o => o.workerId === e.workerId);
      return opt ? opt.key : FREE_KEY;
    }
    if (e.personUserId) {
      const opt = this.personOptions.find(o => o.personUserId === e.personUserId);
      return opt ? opt.key : FREE_KEY;
    }
    return FREE_KEY;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
